/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  GridItem,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import LoadingSpinner from "../../LoadingSpinner";
import {
  cleanSchema,
  reorderAttachmentsAndCaseNotesLast,
} from "../../utils/cleanSchema";
// import { demoInitialSchema } from "../../utils/demoSchema";
import CustomModal from "../Common/CustomModal/CustomModal";
import { AddRepeatAbleItemModal } from "./AddRepeatableItemModal";
import ConditionEditor from "./ConditionEditor";
import { DataCardList } from "./DataCardList";
import { FieldEditorModal } from "./EditFieldModal";
import EditModelConfigModal from "./EditModelConfigModal";
import { NestedDataGrid } from "./NestedDataGrid";
import RulesEdittor from "./RulesEdittor";
import SearchModelList from "./SearchModelList";
import SectionEditor from "./SectionEditor";
import Toolbox from "./Toolbox";
// 🆕 DND KIT imports (as requested)
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaPlus } from "react-icons/fa";
import { usePrompt } from "../../hooks/usePrompt";
import apiClient from "../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../utils/ApiClient/toastService";

function SortableTab({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "pointer",
    userSelect: "none",
  };

  return (
    <Tab ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </Tab>
  );
}

export default function ModelBuilderMainPage() {
  // ---------- original state & hooks ----------
  const [schema, setSchema] = useState({});

  const toast = useToast();
  const [editData, setEditData] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [fieldToEditKey, setFieldToEditKey] = useState(null);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [isSavingSchema, setIsSavingSchema] = useState(false);
  const [pendingNewField, setPendingNewField] = useState(null);

  const [models, setModels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const fetchModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/getConfig?id=MODEL_CONFIGURATION");
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setModels(result);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels(); // initial fetch on mount
  }, []);

  // Unsave warning
  const [initialSchema, setInitialSchema] = useState(null);

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSchema) return false;
    return JSON.stringify(schema) !== JSON.stringify(initialSchema);
  }, [schema, initialSchema]);

  usePrompt(
    hasUnsavedChanges,
    "You have unsaved changes. Are you sure you want to leave this page?",
  );
  useEffect(() => {
    if (schema?.CONFIG && !initialSchema) {
      setInitialSchema(JSON.parse(JSON.stringify(schema)));
    }
  }, [schema]);
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Unsave warning end

  const [addArrayItemOnRepeatableInfo, setAddArrayItemOnRepeatableInfo] =
    useState({});

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isRulesOpen,
    onClose: onRulesClose,
    onOpen: onRulesOpen,
  } = useDisclosure();

  const rulesEditorRef = useRef();
  const { isOpen: RulesOpen, onToggle } = useDisclosure();

  // ---------- 🆕 DND state ----------
  const [tabOrder, setTabOrder] = useState([]);
  const [fieldOrders, setFieldOrders] = useState({});
  const [selectedFields, setSelectedFields] = useState({});
  const [activeTabId, setActiveTabId] = useState(null);

  // ---------- DnD sensors ----------
  const sensors = useSensors(useSensor(PointerSensor));

  const openFieldModal = (value, key, sectionKey, subFieldKey, index = 0) => {
    setEditData({
      ...value,
      sectionKey,
      itemIndex: index,
      fieldKey: key,
      nestedKey: subFieldKey,
    });
    setFieldToEditKey(key);
    setFieldToEdit(schema?.CONFIG?.[sectionKey]?.FIELDS?.[key]);
    setModalOpen(true);
  };

  const handleAddItemOnRepeatableModal = (
    sectionKey,
    fieldKey,
    subFieldKey,
    index,
  ) => {
    setAddArrayItemOnRepeatableInfo({
      sectionKey,
      fieldKey,
      subFieldKey,
      index,
    });
    setAddItemModalOpen(true);
  };

  // ---- Section handlers ----
  const addSection = (sectionKey, sectionData) => {
    setSchema((prev) => {
      if (!prev?.CONFIG) {
        return { ...prev, CONFIG: { [sectionKey]: sectionData } };
      }
      if (prev.CONFIG[sectionKey]) {
        toast({
          title: `Section ${sectionKey} already exists`,
          status: "warning",
          isClosable: true,
        });
        return prev;
      }

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: sectionData,
        },
      };
    });
  };

  const updateSection = (sectionKey, updatedSection) => {
    setSchema((prev) => ({
      ...prev,
      CONFIG: {
        ...prev.CONFIG,
        [sectionKey]: updatedSection,
      },
    }));
  };

  const handleRenameSection = (oldKey, newKey, updatedSection) => {
    setSchema((prev) => {
      if (prev.CONFIG?.[newKey]) {
        toast({
          title: `Section "${newKey}" already exists`,
          status: "error",
          isClosable: true,
        });
        return prev;
      }

      const { [oldKey]: _, ...restConfig } = prev.CONFIG;

      return {
        ...prev,
        CONFIG: {
          ...restConfig,
          [newKey]: updatedSection,
        },
      };
    });

    setTabOrder((prev) => prev.map((k) => (k === oldKey ? newKey : k)));

    setActiveTabId((prev) => (prev === oldKey ? newKey : prev));

    setFieldOrders((prev) => {
      const copy = { ...prev };
      if (copy[oldKey]) {
        copy[newKey] = copy[oldKey];
        delete copy[oldKey];
      }
      return copy;
    });

    setSelectedFields((prev) => {
      const copy = { ...prev };
      if (copy[oldKey]) {
        copy[newKey] = copy[oldKey];
        delete copy[oldKey];
      }
      return copy;
    });
  };

  const removeSection = (sectionKey) => {
    setSchema((prev) => {
      if (!prev?.CONFIG?.[sectionKey]) return prev;

      const newConfig = { ...prev.CONFIG };
      delete newConfig[sectionKey];

      return {
        ...prev,
        CONFIG: newConfig,
      };
    });
  };

  // ---- Field handlers ----
  const addField = (sectionKey, fieldKey, fieldValue = {}) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];

      if (!section) {
        toast({
          title: `Section ${sectionKey} does not exist. Add it first.`,
          status: "error",
          isClosable: true,
        });
        return prev;
      }

      const fields = section.FIELDS || {};

      if (fields[fieldKey]) {
        toast({
          title: `Field ${fieldKey} already exists in ${sectionKey}`,
          status: "warning",
          isClosable: true,
        });
        return prev;
      }

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...fields,
              [fieldKey]: fieldValue,
            },
          },
        },
      };
    });
  };

  const renameField = (
    sectionKey,
    oldKey,
    newKey,
    fieldData,
    itemIndex = null,
    nestedKey = null,
  ) => {
    setSchema((prev) => {
      const section = prev.CONFIG?.[sectionKey];
      if (!section?.FIELDS) return prev;

      // Nested key rename (inside array item)
      if (itemIndex !== null && nestedKey) {
        const currentField = section.FIELDS[oldKey];
        if (!Array.isArray(currentField)) return prev;

        const newArray = [...currentField];
        const item = { ...newArray[itemIndex] };

        // Rebuild item preserving key order
        const rebuiltItem = Object.fromEntries(
          Object.entries(item).map(([k, v]) => {
            if (k === nestedKey) return [newKey, { ...v, ...fieldData }];
            return [k, v];
          }),
        );

        newArray[itemIndex] = rebuiltItem;

        return {
          ...prev,
          CONFIG: {
            ...prev.CONFIG,
            [sectionKey]: {
              ...section,
              FIELDS: {
                ...section.FIELDS,
                [oldKey]: newArray,
              },
            },
          },
        };
      }

      // Top-level field key rename — preserve position
      if (section.FIELDS[newKey]) {
        toast({
          title: `Field key "${newKey}" already exists`,
          status: "error",
          isClosable: true,
        });
        return prev;
      }

      // Rebuild FIELDS preserving original key order
      const rebuiltFields = Object.fromEntries(
        Object.entries(section.FIELDS).map(([k, v]) => {
          if (k === oldKey) return [newKey, fieldData];
          return [k, v];
        }),
      );

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: rebuiltFields,
          },
        },
      };
    });
    // Only for top-level rename
    if (itemIndex === null && !nestedKey) {
      setFieldOrders((prev) => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).map((k) =>
          k === oldKey ? newKey : k,
        ),
      }));
    }
  };

  const updateField = (
    sectionKey,
    fieldKey,
    updatedField,
    itemIndex = 0,
    nestedKey = null,
  ) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) {
        return prev;
      }

      const currentField = section.FIELDS[fieldKey];
      if (!currentField) {
        return prev;
      }

      if (Array.isArray(currentField)) {
        const newArray = [...currentField];
        const item = { ...newArray[itemIndex] };

        if (!item) {
          console.log("❌ Item at index not found");
          return prev;
        }

        const keyToUpdate =
          nestedKey || Object.keys(item).find((k) => k !== "PREVIEW");

        if (!keyToUpdate || !item[keyToUpdate]) {
          return prev;
        }

        // Merge the updated field while preserving existing structure
        item[keyToUpdate] = { ...item[keyToUpdate], ...updatedField };
        newArray[itemIndex] = item;

        return {
          ...prev,
          CONFIG: {
            ...prev.CONFIG,
            [sectionKey]: {
              ...section,
              FIELDS: {
                ...section.FIELDS,
                [fieldKey]: newArray,
              },
            },
          },
        };
      }

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...section.FIELDS,
              [fieldKey]: { ...currentField, ...updatedField },
            },
          },
        },
      };
    });
  };

  const removeField = (sectionKey, fieldKey) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section || !section.FIELDS?.[fieldKey]) return prev;

      const newFields = { ...section.FIELDS };
      delete newFields[fieldKey];

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: newFields,
          },
        },
      };
    });
  };

  const addArrayItem = (sectionKey, fieldKey, itemTemplate = null) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      const current = Array.isArray(section.FIELDS[fieldKey])
        ? [...section.FIELDS[fieldKey]]
        : [];

      // If no template passed, use the first item as template
      if (!itemTemplate && current.length > 0) {
        const templateItem = current[0];
        // Create a new item copying keys but clearing values
        const newItem = Object.fromEntries(
          Object.entries(templateItem).map(([k, v]) => {
            if (typeof v === "object" && v !== null && "TYPE" in v) {
              return [k, { ...v }]; // preserve TYPE, MESSAGE, etc.
            }
            return [k, v];
          }),
        );
        current.push(newItem);
      } else if (itemTemplate) {
        current.push(itemTemplate);
      } else {
        current.push({});
      }

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...section.FIELDS,
              [fieldKey]: current,
            },
          },
        },
      };
    });
  };

  const deleteArrayItem = (sectionKey, fieldKey, index, subFieldKey) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      const arrayField = section.FIELDS[fieldKey];
      if (!Array.isArray(arrayField)) return prev;

      const updatedArray = arrayField.map((item, i) => {
        if (i !== index) return item;
        // Remove the sub-field
        const { [subFieldKey]: _, ...rest } = item;
        return rest;
      });

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...section.FIELDS,
              [fieldKey]: updatedArray,
            },
          },
        },
      };
    });
  };

  const deleteArrayField = (sectionKey, fieldKey, itemIndex) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      const currentArray = section.FIELDS[fieldKey];
      if (!Array.isArray(currentArray)) return prev;

      const newArray = currentArray.filter((_, idx) => idx !== itemIndex);

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...section.FIELDS,
              [fieldKey]: newArray,
            },
          },
        },
      };
    });
  };

  const addToRepeatableArray = (
    sectionKey,
    fieldKey,
    subFieldValue,
    index = 0,
  ) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      const arrayField = Array.isArray(section.FIELDS?.[fieldKey])
        ? [...section.FIELDS[fieldKey]]
        : [];

      if (!arrayField[index]) return prev; // no item at index

      const { SUBFIELD, ...valueWithoutSubfield } = subFieldValue;
      const updatedItem = {
        ...arrayField[index],
        [SUBFIELD]: valueWithoutSubfield,
      };

      const updatedArray = [...arrayField];
      updatedArray[index] = updatedItem;

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            FIELDS: {
              ...section.FIELDS,
              [fieldKey]: updatedArray,
            },
          },
        },
      };
    });
  };

  const updateCondition = (sectionKey, updatedCondition) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            CONDITION: updatedCondition,
          },
        },
      };
    });
  };

  const deleteCondition = (sectionKey) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey];
      if (!section) return prev;

      const { CONDITION, ...rest } = section;

      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: rest,
        },
      };
    });
  };

  const addConditionToSection = (sectionKey) => {
    setSchema((prev) => {
      const section = prev?.CONFIG?.[sectionKey] || {};
      if (section.CONDITION) {
        toast({
          title: `Condition already exists in ${sectionKey}`,
          status: "warning",
          isClosable: true,
        });
        return prev;
      }
      return {
        ...prev,
        CONFIG: {
          ...prev.CONFIG,
          [sectionKey]: {
            ...section,
            CONDITION: {},
          },
        },
      };
    });
  };

  const handleSaveModelSchema = async () => {
    setIsSavingSchema(true);

    try {
      const latestRules =
        rulesEditorRef.current?.getCurrentRules?.() || schema.RULES;
      const FinalConfig = reorderAttachmentsAndCaseNotesLast(schema.CONFIG);
      const response = await apiClient.put(
        "/model",
        {
          CONFIG: cleanSchema(FinalConfig),
          RULES: latestRules,
          id: schema?.CONFIG_ID,
        },
        {
          params: { id: schema?.id },
        },
      );

      toastService.success(response.data);
      setInitialSchema(JSON.parse(JSON.stringify(schema)));
    } catch (err) {
      console.error("Save schema error:", err);
    } finally {
      setIsSavingSchema(false);
    }
  };

  useEffect(() => {
    const keys = Object.keys(schema.CONFIG ?? {});

    setTabOrder((prev) => {
      if (!prev || prev.length === 0) return keys;

      const existing = prev.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !existing.includes(k));
      return [...existing, ...added];
    });

    // initialize fieldOrders for each section
    setFieldOrders((prev) => {
      const newMap = { ...(prev || {}) };
      keys.forEach((k) => {
        const fkeys = Object.keys(schema.CONFIG?.[k]?.FIELDS ?? {});
        if (!newMap[k]) newMap[k] = fkeys;
        else {
          const existingFields = newMap[k].filter((f) => fkeys.includes(f));
          const addedFields = fkeys.filter((f) => !existingFields.includes(f));
          newMap[k] = [...existingFields, ...addedFields];
        }
      });

      Object.keys(newMap).forEach((k) => {
        if (!keys.includes(k)) delete newMap[k];
      });
      return newMap;
    });

    setSelectedFields((prev) => {
      const copy = { ...(prev || {}) };
      keys.forEach((k) => {
        if (!copy[k]) copy[k] = [];

        const valid = (copy[k] || []).filter((s) =>
          Object.prototype.hasOwnProperty.call(
            schema.CONFIG?.[k]?.FIELDS ?? {},
            s,
          ),
        );
        copy[k] = valid;
      });
      return copy;
    });
  }, [schema.CONFIG]);

  useEffect(() => {
    if (tabOrder.length && !activeTabId) {
      setActiveTabId(tabOrder[0]);
    }
  }, [tabOrder]);

  const handleTabDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setTabOrder((prevOrder) => {
      const oldIdx = prevOrder.indexOf(active.id);
      const newIdx = prevOrder.indexOf(over.id);
      if (oldIdx === -1 || newIdx === -1) return prevOrder;

      const newOrder = arrayMove(prevOrder, oldIdx, newIdx);

      // ✅ Keep same tab active after reorder
      setActiveTabId((prevActiveId) => prevActiveId);

      // ✅ Update schema in local state only
      setSchema((prevSchema) => {
        const config = prevSchema.CONFIG || {};
        const reorderedEntries = newOrder
          .filter((k) => Object.prototype.hasOwnProperty.call(config, k))
          .map((k) => [k, config[k]]);
        Object.keys(config).forEach((k) => {
          if (!newOrder.includes(k)) reorderedEntries.push([k, config[k]]);
        });
        return { ...prevSchema, CONFIG: Object.fromEntries(reorderedEntries) };
      });

      return newOrder;
    });
  };

  const activeIndex = tabOrder.findIndex((key) => key === activeTabId);

  const buildOrderedSections = () => {
    const entries = Object.entries(schema.CONFIG ?? {}).filter(
      ([_, s]) => !s?.UI_SKIP,
    );
    if (!tabOrder || tabOrder.length === 0) return entries;

    const ordered = tabOrder
      .map((k) => entries.find(([key]) => key === k))
      .filter(Boolean);
    entries.forEach((e) => {
      if (!ordered.some(([k]) => k === e[0])) ordered.push(e);
    });
    return ordered;
  };

  const orderedSections = buildOrderedSections();
  const generateNewFieldKey = (sectionKey) => {
    const existingKeys = Object.keys(
      schema?.CONFIG?.[sectionKey]?.FIELDS ?? {},
    );
    let counter = 1;
    let newKey = `NEW_FIELD_${counter}`;
    while (existingKeys.includes(newKey)) {
      counter++;
      newKey = `NEW_FIELD_${counter}`;
    }
    return newKey;
  };
  const DEFAULT_NEW_FIELD = {
    MESSAGE: "New Field",
    TYPE: "String",
    VALUE: "",
    EDITABLE: true,
    VISIBLE: true,
    TOOLTIP: "",
  };

  // ------------------- RENDER -------------------
  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" w="100%" mb={1}>
        <SearchModelList
          setSchema={setSchema}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          models={models}
          schema={schema}
        />
        <Box display="flex" alignItems="center" gap={2}>
          {schema?.RULES && (
            <Button
              onClick={onToggle}
              size="sm"
              width="200px"
              rightIcon={RulesOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            >
              Expand Rules
            </Button>
          )}

          <Button size="sm" w="150px" onClick={onOpen}>
            Edit config
          </Button>
          {schema?.CONFIG && Object.keys(schema.CONFIG ?? {}).length > 0 && (
            <Button
              size="md"
              variant="primary"
              width="100%"
              onClick={handleSaveModelSchema}
              isLoading={isSavingSchema}
              loadingText="Saving..."
            >
              Save Schema
            </Button>
          )}
        </Box>
      </Flex>

      {loading && <LoadingSpinner />}
      {schema?.RULES && !loading && (
        <Box my={2}>
          <Collapse in={RulesOpen} animateOpacity>
            <Box>
              <RulesEdittor
                schema={schema}
                setSchema={setSchema}
                onRulesClose={onRulesClose}
                onToggle={onToggle}
                ref={rulesEditorRef}
              />
            </Box>
          </Collapse>
        </Box>
      )}
      {schema?.CONFIG &&
        Object.keys(schema.CONFIG ?? {}).length > 0 &&
        !loading && (
          <Grid templateColumns="360px 1fr" gap={6} height="80vh">
            {/* Left Toolbox */}
            <Box overflowY="auto" border="1px solid #eee" p={2}>
              <Toolbox
                schema={schema.CONFIG}
                onAddSection={addSection}
                onRemoveSection={removeSection}
                onAddField={addField}
                onRemoveField={removeField}
                onUpdateField={updateField}
              />
            </Box>

            {/* Right Sections & Fields */}
            <Box
              overflowY="auto"
              maxHeight="80vh"
              border="1px solid #eee"
              p={4}
              borderRadius="md"
              position="relative"
            >
              {/* 🆕 DND: Tabs */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTabDragEnd}
              >
                <SortableContext
                  items={
                    tabOrder.length
                      ? tabOrder
                      : Object.keys(schema.CONFIG || {})
                  }
                  strategy={rectSortingStrategy}
                >
                  {" "}
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FaPlus} />}
                    onClick={() =>
                      addSection(`Section_${Date.now()}`, {
                        MESSAGE: "New Section",
                        FIELDS: {},
                      })
                    }
                    textAlign="right"
                    position="absolute"
                    right={2}
                    top={0}
                    mb={2}
                  >
                    Add Section
                  </Button>
                  <Tabs
                    variant="enclosed"
                    // isFitted
                    index={activeIndex}
                    onChange={(i) => setActiveTabId(tabOrder[i])}
                    mt={2}
                  >
                    <TabList flexWrap="wrap">
                      {orderedSections.map(([sectionKey, section]) => (
                        <SortableTab key={sectionKey} id={sectionKey}>
                          {section.MESSAGE}
                        </SortableTab>
                      ))}
                    </TabList>

                    <TabPanels>
                      {orderedSections.map(([sectionKey, section]) => {
                        const fieldsObj = section?.FIELDS || {};
                        const fieldKeysOrdered = fieldOrders[sectionKey]?.length
                          ? fieldOrders[sectionKey].filter((k) =>
                              Object.prototype.hasOwnProperty.call(
                                fieldsObj,
                                k,
                              ),
                            )
                          : Object.keys(fieldsObj);

                        const fieldsArray = fieldKeysOrdered.map((k) => ({
                          key: k,
                          value: fieldsObj[k],
                        }));

                        // Split array vs single fields
                        const arrayFields = fieldsArray.filter(
                          ({ value }) =>
                            Array.isArray(value) &&
                            section?.MESSAGE !== "Case Links" &&
                            section?.MESSAGE !== "ATTACHMENTS",
                        );
                        const singleFields = fieldsArray.filter(
                          ({ value }) =>
                            !Array.isArray(value) ||
                            section?.MESSAGE === "Case Links" ||
                            section?.MESSAGE === "ATTACHMENTS",
                        );

                        return (
                          <TabPanel key={sectionKey} p={0} w="100%">
                            <Grid templateColumns="1fr" gap={4} w="100%">
                              {/* Section header/editor */}
                              <SectionEditor
                                sectionKey={sectionKey}
                                section={section}
                                onUpdate={updateSection}
                                onRename={handleRenameSection}
                                onDelete={removeSection}
                                addConditionToSection={addConditionToSection}
                              />

                              {/* Section condition */}
                              {section.CONDITION && (
                                <ConditionEditor
                                  sectionKey={sectionKey}
                                  condition={section.CONDITION}
                                  onUpdate={updateCondition}
                                  onDelete={deleteCondition}
                                  mainSchemaData={schema}
                                  fieldKey={sectionKey}
                                />
                              )}

                              {/* Array fields: keep them outside any DnD context */}
                              {arrayFields.map(
                                ({ key: fieldKey, value: fieldValue }) => (
                                  <Box
                                    key={fieldKey}
                                    w="100%"
                                    bg="gray.200"
                                    borderRadius="md"
                                    boxShadow="1px 1px 8px 0px #fff  inset"
                                  >
                                    <DataCardList
                                      data={fieldValue}
                                      tabKey={sectionKey}
                                      fieldKey={fieldKey}
                                      onArrayChange={(val) =>
                                        updateField(sectionKey, fieldKey, val)
                                      }
                                      deleteArrayField={(itemIndex) =>
                                        deleteArrayField(
                                          sectionKey,
                                          fieldKey,
                                          itemIndex,
                                        )
                                      }
                                      onDeleteItem={(index, subFieldKey) =>
                                        deleteArrayItem(
                                          sectionKey,
                                          fieldKey,
                                          index,
                                          subFieldKey,
                                        )
                                      }
                                      onAddItem={() =>
                                        addArrayItem(sectionKey, fieldKey, {})
                                      }
                                      onEdit={(value, index, subFieldKey) =>
                                        openFieldModal(
                                          value,
                                          fieldKey,
                                          sectionKey,
                                          subFieldKey,
                                          index,
                                        )
                                      }
                                      addRepeatableItem={(index, subFieldKey) =>
                                        handleAddItemOnRepeatableModal(
                                          sectionKey,
                                          fieldKey,
                                          subFieldKey,
                                          index,
                                        )
                                      }
                                      // 🆕 ADD THIS PROP:
                                      onReorder={(reorderedData) => {
                                        // Update the schema with the reordered array
                                        setSchema((prev) => {
                                          const section =
                                            prev?.CONFIG?.[sectionKey];
                                          if (!section) return prev;

                                          return {
                                            ...prev,
                                            CONFIG: {
                                              ...prev.CONFIG,
                                              [sectionKey]: {
                                                ...section,
                                                FIELDS: {
                                                  ...section.FIELDS,
                                                  [fieldKey]: reorderedData,
                                                },
                                              },
                                            },
                                          };
                                        });
                                      }}
                                    />
                                  </Box>
                                ),
                              )}

                              {/* Only single fields are draggable */}
                              {singleFields.length > 0 && (
                                <NestedDataGrid
                                  tabKey={sectionKey}
                                  data={Object.fromEntries(
                                    singleFields.map(({ key, value }) => [
                                      key,
                                      value,
                                    ]),
                                  )}
                                  formState={{}}
                                  onInputChange={(key, val) =>
                                    updateField(sectionKey, key, { VALUE: val })
                                  }
                                  onEdit={(value, key) =>
                                    openFieldModal(value, key, sectionKey)
                                  }
                                  onDelete={(fieldKey) =>
                                    removeField(sectionKey, fieldKey)
                                  }
                                  onReorder={(newOrderedArray) => {
                                    // Update field order for this section
                                    setFieldOrders((prev) => ({
                                      ...prev,
                                      [sectionKey]: newOrderedArray.map(
                                        (f) => f.key,
                                      ),
                                    }));

                                    // Persist reordered fields in schema
                                    const newFieldsObj = Object.fromEntries(
                                      newOrderedArray.map(({ key, value }) => [
                                        key,
                                        value,
                                      ]),
                                    );
                                    updateSection(sectionKey, {
                                      ...section,
                                      FIELDS: {
                                        ...Object.fromEntries(
                                          arrayFields.map(({ key, value }) => [
                                            key,
                                            value,
                                          ]),
                                        ), // preserve array fields
                                        ...newFieldsObj, // updated single fields
                                      },
                                    });
                                  }}
                                  onAddNew={(tabKey, fieldKey, fieldData) => {
                                    addField(tabKey, fieldKey, fieldData);
                                  }}
                                />
                              )}
                              {/* Add New Field Button */}
                              <GridItem colSpan={1}>
                                <Button
                                  variant="outline"
                                  borderStyle="dashed"
                                  borderWidth={2}
                                  width="100%"
                                  minHeight="60px"
                                  colorScheme="blue"
                                  leftIcon={<Icon as={FaPlus} />}
                                  _hover={{
                                    bg: "blue.50",
                                    borderColor: "blue.400",
                                  }}
                                  onClick={() => {
                                    const newFieldKey =
                                      generateNewFieldKey(sectionKey);

                                    addField(
                                      sectionKey,
                                      newFieldKey,
                                      DEFAULT_NEW_FIELD,
                                    );

                                    setPendingNewField({
                                      sectionKey,
                                      fieldKey: newFieldKey,
                                    });

                                    openFieldModal(
                                      DEFAULT_NEW_FIELD,
                                      newFieldKey,
                                      sectionKey,
                                    );
                                  }}
                                >
                                  Add Field
                                </Button>
                              </GridItem>
                            </Grid>
                          </TabPanel>
                        );
                      })}
                    </TabPanels>
                  </Tabs>
                </SortableContext>
              </DndContext>
            </Box>
          </Grid>
        )}

      <FieldEditorModal
        isOpen={isModalOpen}
        onClose={() => {
          if (pendingNewField) {
            removeField(pendingNewField.sectionKey, pendingNewField.fieldKey);
            setPendingNewField(null);
          }
          setModalOpen(false);
        }}
        fieldData={editData}
        mainSchemaData={schema}
        sectionKey={editData?.sectionKey}
        onSave={(updatedField) => {
          console.log("💾 FieldEditorModal onSave called with:", {
            updatedField,
            editData,
          });

          const { sectionKey, itemIndex, nestedKey } = editData;
          const newKey = updatedField.__NEW_FIELD_KEY__;
          const newNestedKey = updatedField.__NEW_NESTED_KEY__;
          delete updatedField.__NEW_FIELD_KEY__;
          delete updatedField.__NEW_NESTED_KEY__;

          delete updatedField.fieldKey;
          delete updatedField.itemIndex;
          delete updatedField.nestedKey;
          delete updatedField.sectionKey;

          if (nestedKey) {
            if (newNestedKey && newNestedKey !== nestedKey) {
              renameField(
                sectionKey,
                fieldToEditKey,
                newNestedKey,
                updatedField,
                itemIndex,
                nestedKey,
              );
            } else {
              updateField(
                sectionKey,
                fieldToEditKey,
                updatedField,
                itemIndex,
                nestedKey,
              );
            }
          } else {
            if (newKey && newKey !== fieldToEditKey) {
              renameField(sectionKey, fieldToEditKey, newKey, updatedField);
            } else {
              updateField(sectionKey, fieldToEditKey, updatedField, itemIndex);
            }
          }

          setPendingNewField(null);
          setModalOpen(false);
        }}
      />

      <AddRepeatAbleItemModal
        isOpen={addItemModalOpen}
        onClose={() => setAddItemModalOpen(false)}
        onSave={(data) => {
          addToRepeatableArray(
            addArrayItemOnRepeatableInfo?.sectionKey,
            addArrayItemOnRepeatableInfo?.fieldKey,
            data,
            addArrayItemOnRepeatableInfo?.index,
          );
          setAddItemModalOpen(false);
        }}
      />

      <CustomModal
        size="6xl"
        title="configuration edit"
        isOpen={isOpen}
        onClose={onClose}
        bodyContent={
          <EditModelConfigModal
            data={models}
            setData={setModels}
            loading={loading}
            error={error}
            onRefresh={fetchModels}
          />
        }
      />
    </Box>
  );
}
